/**
 * /care-after-discharge-vietnam
 *
 * SEO landing page — Care After Hospital Discharge in Vietnam.
 *
 * Reuses the same data-driven section components, hero pattern, JSON-LD
 * schema, palette and editorial/disclaimer blocks as the other patient-
 * support landing pages — no new design work.
 *
 * Strict copy compliance:
 *   • VietClinics is an independent healthcare coordination platform.
 *   • Not a hospital, clinic, nursing agency, doctor or emergency service.
 *   • Coordinator language — "helps coordinate / supports / connects with".
 *   • No guarantees of recovery, no medical advice, no fixed pricing.
 *   • Hero contact + admin-managed image via the existing infoLandings override.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ShieldCheck, MessageCircle, Heart, Users, Building2,
  AlertTriangle,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentIntro, UrgentCardGrid, UrgentSteps, UrgentFaq, UrgentEditorial,
  UrgentCtaBand,
} from '../components/UrgentCareSections';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { useCatalog } from '../context/CatalogContext';
import { getInfoLandingImage } from '../lib/imageOverrides';

const SITE = 'https://vietclinics.com';
const SLUG = '/care-after-discharge-vietnam';

const HERO_IMAGE =
  'https://images.pexels.com/photos/7551668/pexels-photo-7551668.jpeg?auto=compress&cs=tinysrgb&w=1400';
const HERO_FALLBACK =
  'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1400';

const SUPPORT_AREAS = [
  { title: 'Hotel room recovery support',        body: 'Practical day-to-day support in your hotel room or serviced apartment during the first days after discharge — help with comfort, organisation and check-ins.' },
  { title: 'Nurse visit coordination',           body: 'Where appropriate and available, we help coordinate licensed nurse visits through trusted clinics, hospitals and medical providers in Vietnam.' },
  { title: 'Wound care & dressing support',      body: 'We help coordinate wound dressing checks or follow-up care through suitable medical providers. Doctor instructions should always be followed.' },
  { title: 'Medication reminders',               body: 'Gentle help keeping track of medication times, dosage instructions and refill needs based on your discharge plan.' },
  { title: 'Help with clothing & personal care', body: 'Light, respectful support with day-to-day tasks that can be hard after surgery — changing clothes, washing up and moving around safely.' },
  { title: 'Mobility support',                   body: 'Help moving around your hotel room, getting in and out of bed, and walking short distances safely during early recovery.' },
  { title: 'Hydration & meal support',           body: 'Help arranging suitable meals, hydration and rest schedules in line with what your doctor recommends after discharge.' },
  { title: 'Doctor or clinic follow-up',         body: 'We help schedule and coordinate follow-up appointments, dressing checks and doctor reviews at your treating clinic or hospital.' },
  { title: 'Translation & communication',        body: 'English-speaking support and translator coordination so instructions from doctors, nurses and pharmacies are clearly understood.' },
  { title: 'Transport to follow-ups',            body: 'We help coordinate transport between your hotel and the clinic or hospital for follow-up visits, dressing changes or doctor reviews.' },
];

const WHO_ITS_FOR = [
  'Cosmetic surgery patients',
  'Breast surgery, facelift, rhinoplasty, eyelid surgery, liposuction, tummy tuck & body contouring patients',
  'Dental surgery & dental implant patients',
  'Hair transplant patients',
  'Eye surgery patients',
  'Health check-up patients needing follow-up',
  'Older patients or patients with mobility needs',
  'International patients travelling alone',
  'Patients staying in hotels or serviced apartments after treatment',
];

const STEPS = [
  { title: 'Tell us what treatment you had',          body: 'We ask about your procedure, discharge plan, location and recovery needs.' },
  { title: 'We review what support may be suitable',  body: 'We help understand whether you need basic hotel support, nurse care, translation, transport or doctor follow-up.' },
  { title: 'We coordinate with trusted providers',    body: 'Where medical support is required, we help connect you with suitable licensed clinics, hospitals, doctors or nurses.' },
  { title: 'You recover with clearer support',        body: 'You have someone helping you understand the next steps, follow-up times and who to contact if you are worried.' },
];

const FAQ = [
  { q: 'Can I get support in my hotel room after surgery in Vietnam?',
    a: 'Yes. VietClinics can help coordinate hotel room recovery support after surgery or hospital discharge, depending on your location, treatment and needs.' },
  { q: 'Can VietClinics arrange a nurse visit to my hotel?',
    a: 'Where available, VietClinics can help coordinate a licensed nurse visit to your hotel through trusted clinics, hospitals or medical providers in Vietnam.' },
  { q: 'Is aftercare support useful if I travel alone?',
    a: 'Yes. Patients travelling alone often find aftercare support helpful, especially during the first few days after discharge when moving around, changing clothes, taking medication and attending follow-ups can be difficult.' },
  { q: 'Do you provide wound dressing support?',
    a: 'VietClinics can help coordinate wound dressing support or follow-up care through suitable medical providers. Your doctor\u2019s instructions should always be followed.' },
  { q: 'Can you help after cosmetic surgery in Vietnam?',
    a: 'Yes. Cosmetic surgery aftercare can be useful after procedures such as breast surgery, facelift, eyelid surgery, liposuction, tummy tuck, rhinoplasty and body contouring — VietClinics helps coordinate the right recovery support for your case.' },
  { q: 'Can you help with transport back to the clinic?',
    a: 'Yes. VietClinics can help coordinate transport for follow-up appointments, dressing checks or doctor reviews.' },
  { q: 'Is VietClinics a nursing company?',
    a: 'No. VietClinics is an independent healthcare coordination platform. We help connect patients with suitable healthcare providers and support services.' },
];

const RELATED_LINKS = [
  { to: '/cosmetic-surgery-in-vietnam',     label: 'Cosmetic Surgery in Vietnam',         sub: 'Korean & US-trained surgeons, JCI-accredited centres' },
  { to: '/dental-treatments-in-vietnam',    label: 'Dental Treatment in Vietnam',         sub: 'Implants, veneers and full-mouth restoration' },
  { to: '/health-checkups-in-vietnam',      label: 'Health Checkups in Vietnam',          sub: 'Executive screenings & preventive packages' },
  { to: '/urgent-medical-care',             label: 'Urgent Medical Care in Vietnam',      sub: 'Hotel doctor, IV therapy & English-speaking telehealth' },
  { to: '/doctor-to-hotel-vietnam',         label: 'Doctor-to-Hotel in Vietnam',          sub: 'Same-day non-emergency doctor visits to your room' },
  { to: '/medical-tourism-support-vietnam', label: 'Medical Tourism Support in Vietnam',  sub: 'Treatment, travel & patient coordination for international patients' },
  { to: '/request-treatment',               label: 'Request Appointment',                 sub: 'Send your case to a coordinator' },
];

const EDITORIAL = {
  reviewedBy: 'VietClinics Patient Concierge Team',
  role: 'Patient support & treatment coordinators',
  methodology:
    'Aftercare partners are sourced from our verified network and vetted against an internal checklist covering qualifications, language support, hygiene standards and aftercare protocols. Reviewed quarterly.',
  lastUpdated: '2026-02-16',
};

const CareAfterDischargeVietnamPage = () => {
  const { contactInfo } = useContactInfo();
  const { overrides } = useCatalog();
  const heroImage = getInfoLandingImage(overrides, 'care-after-discharge', HERO_IMAGE);
  const whatsappHref = getWhatsAppHref(
    contactInfo,
    'Hi VietClinics, I would like aftercare support after hospital discharge in Vietnam.'
  );

  const canonical = `${SITE}${SLUG}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${canonical}#service`,
        serviceType: 'Aftercare & Post-Discharge Recovery Coordination',
        provider: { '@type': 'Organization', name: 'VietClinics' },
        areaServed: [{ '@type': 'Country', name: 'Vietnam' }],
        url: canonical,
        description:
          'Independent coordination of aftercare support for international patients in Vietnam — hotel room recovery support, nurse visit coordination, wound care follow-up, medication reminders, translation, and transport to follow-up appointments after hospital discharge.',
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: 'Care After Hospital Discharge in Vietnam',
        description:
          'VietClinics helps coordinate aftercare and recovery support for international patients after hospital discharge in Vietnam — hotel room support, nurse visits, wound dressing, medication reminders, follow-up coordination and translation.',
        inLanguage: 'en',
        dateModified: EDITORIAL.lastUpdated,
        lastReviewed: EDITORIAL.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: EDITORIAL.reviewedBy },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Care After Hospital Discharge', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Care After Hospital Discharge in Vietnam | Hotel Recovery Support</title>
        <meta
          name="description"
          content="Need patient support after hospital discharge in Vietnam? VietClinics coordinates aftercare services in Vietnam — hotel room recovery support, nurse visits to your hotel, wound dressing, medication reminders, post-surgery care and medical tourism aftercare for international patients."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Care After Hospital Discharge in Vietnam | VietClinics" />
        <meta
          property="og:description"
          content="Hotel room recovery support, nurse visit coordination, wound dressing, medication reminders and post-surgery care for international patients — patient support after hospital discharge in Vietnam."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid="aftercare-hero">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)',
          }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                Aftercare · Recovery support · Vietnam
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-3">
                Care After Hospital Discharge in Vietnam
              </h1>
              <p className="text-xl font-semibold text-[#1A8F8A] mb-5">
                Support in your hotel room when you need it most.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                Leaving the hospital does not always mean you are ready to manage
                everything alone. After surgery, dental treatment, cosmetic
                procedures or a medical stay in Vietnam, simple things like
                changing clothes, checking dressings, taking medication, moving
                around safely or getting back to the clinic can feel much harder
                when you are recovering alone. VietClinics helps coordinate
                practical aftercare and recovery support so patients feel safer,
                more comfortable and better supported after discharge.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/request-treatment"
                  className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold rounded-full px-6 py-3.5 transition-colors"
                  data-testid="aftercare-cta-request"
                >
                  Request Aftercare Support
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {whatsappHref ? (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-[#1FA7A1] font-bold rounded-full px-6 py-3.5 transition-colors"
                    data-testid="aftercare-cta-coordinator"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Speak to a Patient Coordinator
                  </a>
                ) : (
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-[#1FA7A1] font-bold rounded-full px-6 py-3.5 transition-colors"
                    data-testid="aftercare-cta-coordinator"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Speak to a Patient Coordinator
                  </Link>
                )}
              </div>
            </div>
            <div
              className="relative rounded-3xl overflow-hidden bg-slate-100"
              style={{ aspectRatio: '4 / 3' }}
            >
              <img
                src={heroImage}
                onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
                alt="Patient recovering in hotel room with patient support coordinator after hospital discharge in Vietnam"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100" data-testid="aftercare-trust">
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck, label: 'Verified provider network' },
              { icon: Users,       label: 'English-speaking coordinators' },
              { icon: Building2,   label: 'Independent — not a nursing agency' },
              { icon: Heart,       label: 'Support after you leave hospital' },
            ].map(({ icon: Icon, label }, i) => (
              <div key={label} className="flex items-center gap-3" data-testid={`aftercare-trust-${i + 1}`}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DCEFEC' }}>
                  <Icon className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 1. WHY AFTERCARE MATTERS ─────────────────────────────────────── */}
      <UrgentIntro
        heading="Recovery does not stop when you leave the hospital"
        paragraphs={[
          'The first few days after discharge are often the most important. Patients may still feel tired, sore, swollen, dizzy, or unsure about what is normal. Some patients may have dressings, medication, drains, stitches, bruising, swelling, or follow-up appointments.',
          'For international patients travelling alone, this can feel stressful. Patient support after hospital discharge — and well-organised post-surgery care in your hotel room — can make a real difference during the first 24 to 72 hours.',
          'VietClinics helps coordinate the right support around you so you are not left trying to manage everything by yourself.',
        ]}
        testId="aftercare-why"
      />

      {/* ── 2. WHAT WE CAN HELP COORDINATE ───────────────────────────────── */}
      <UrgentCardGrid
        eyebrow="What VietClinics can help coordinate"
        heading="Practical recovery support after hospital discharge"
        items={SUPPORT_AREAS}
        testId="aftercare-areas"
      />

      <section className="py-6 bg-white" data-testid="aftercare-areas-intro">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-sm text-slate-500 leading-relaxed">
            Depending on your treatment, location and recovery needs, VietClinics
            can help coordinate practical aftercare services in Vietnam — from
            simple hotel room recovery support to cosmetic surgery aftercare and
            medical tourism aftercare for international patients.
          </p>
        </div>
      </section>

      {/* ── 3. WHO THIS IS FOR ───────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="aftercare-who">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Who this is for
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Aftercare support for patients recovering in Vietnam
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {WHO_ITS_FOR.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`aftercare-who-${i + 1}`}
              >
                <Users className="h-5 w-5 text-[#1A8F8A] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. HOW IT WORKS ──────────────────────────────────────────────── */}
      <UrgentSteps
        eyebrow="How VietClinics works"
        heading="A simple 4-step aftercare coordination process"
        steps={STEPS}
        testId="aftercare-steps"
      />

      {/* ── 5. TRUST SECTION ─────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="aftercare-trust-text">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Independent &amp; coordinated
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Independent support, coordinated around your recovery
          </h2>
          <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
            VietClinics does not replace your doctor, hospital or medical team.
            Our role is to help coordinate communication, appointments, aftercare
            support, transport, translation and trusted provider connections so
            your recovery in Vietnam feels more organised and less stressful.
            For emergencies or life-threatening symptoms, patients should contact
            emergency services or go directly to the nearest hospital.
          </p>
        </div>
      </section>

      {/* ── 6. EMOTIONAL SECTION ─────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F0FAF8]" data-testid="aftercare-emotional">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            You are not alone
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            You do not have to recover alone
          </h2>
          <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
            Many patients travel to Vietnam by themselves for treatment. Even
            when the procedure goes well, the recovery period can feel lonely.
            Having someone to check on you, help organise things, speak to the
            clinic, remind you about follow-ups or arrange suitable support can
            make a big difference. From simple things to harder things,
            VietClinics is here to help make recovery feel more supported.
          </p>
        </div>
      </section>

      {/* ── 7. FAQ ───────────────────────────────────────────────────────── */}
      <UrgentFaq
        heading="Care after hospital discharge in Vietnam — frequently asked"
        items={FAQ}
        testId="aftercare-faq"
      />

      {/* ── RELATED LINKS ────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="aftercare-related">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Related VietClinics support
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Plan, treat &amp; recover with VietClinics
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RELATED_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl p-5 transition-colors flex items-start justify-between gap-4"
                data-testid={`aftercare-related-${link.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">{link.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{link.sub}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── EDITORIAL / E-E-A-T ─────────────────────────────────────────── */}
      <UrgentEditorial editorial={EDITORIAL} testId="aftercare-editorial" />

      {/* ── INDEPENDENT PLATFORM DISCLAIMER ─────────────────────────────── */}
      <section className="py-10 bg-white" data-testid="aftercare-disclaimer-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <div
            className="bg-amber-50/60 border border-amber-100 rounded-2xl p-5 lg:p-6 flex gap-3"
            data-testid="aftercare-disclaimer"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed text-amber-900">
              <p className="font-bold mb-1">Important notice</p>
              <p>
                VietClinics is an independent healthcare coordination platform.
                VietClinics is <strong>not a hospital, clinic, nursing agency,
                doctor or emergency service</strong> and does not provide medical
                diagnosis. Patients should always follow their doctor&rsquo;s
                instructions. For life-threatening emergencies in Vietnam, dial
                <strong> 115</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. FINAL CTA ─────────────────────────────────────────────────── */}
      <UrgentCtaBand
        heading="Need support after leaving hospital in Vietnam?"
        subheading="Whether you are planning treatment or have already been discharged, VietClinics can help you understand what aftercare support may be available."
        testId="aftercare-final-cta"
      />

      <Footer />
    </div>
  );
};

export default CareAfterDischargeVietnamPage;
